import React, { useCallback, useState } from 'react';
import { Button } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ScheduleTimePicker } from './ScheduleSendControl';
import { formatScheduledEt, isoToScheduleValue, validateScheduleValue } from '../../lib/scheduleSend';

const { FiClock, FiAlertTriangle, FiX } = FiIcons;

const DISMISSED_KEY = 'scheduledEmails.dismissed';

function readDismissed() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

/** Worker last_error → plain-language reason (see send-scheduled-emails). */
export function scheduledEmailProblem(row) {
  const raw = String(row.last_error || '');
  if (/delivery unknown/i.test(raw)) {
    return 'Delivery unknown. Check Brevo or ask the recipient before resending';
  }
  if (row.status === 'cancelled') {
    if (/email changed/i.test(raw)) return "Cancelled: lead's email changed since scheduling";
    if (/no longer has an email/i.test(raw)) return 'Cancelled: lead no longer has an email address';
    if (/status is dnc/i.test(raw)) return 'Cancelled: lead is marked do-not-contact';
    if (/no longer exists/i.test(raw)) return 'Cancelled: lead no longer exists';
    return `Cancelled: ${raw}`;
  }
  if (row.status === 'failed') {
    const n = row.attempts || 0;
    return n > 0 ? `Failed after ${n} attempt${n === 1 ? '' : 's'}` : `Failed: ${raw}`;
  }
  return raw;
}

/**
 * Pending rows, plus worker-cancelled / failed rows (those with last_error).
 * Self-cancelled (no last_error) and sent rows stay hidden; sent emails show in history.
 */
function isVisible(row) {
  if (row.status === 'pending') return true;
  return (row.status === 'cancelled' || row.status === 'failed') && !!row.last_error;
}

export default function ScheduledEmailsList({ rows, onCancel, onReschedule }) {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);

  const dismiss = useCallback((id) => {
    setDismissed((prev) => {
      const next = new Set(prev).add(id);
      try {
        sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...next]));
      } catch {
        /* storage unavailable: hidden until reload */
      }
      return next;
    });
  }, []);

  const run = async (id, fn) => {
    setBusyId(id);
    try {
      await fn();
    } finally {
      setBusyId(null);
    }
  };

  const visible = (rows || []).filter((r) => isVisible(r) && !dismissed.has(r.id));
  if (!visible.length) return null;

  return (
    <ul className="space-y-2 mb-4">
      {visible.map((row) => {
        const busy = busyId === row.id;
        const isPending = row.status === 'pending';
        const editCheck = editing?.id === row.id ? validateScheduleValue(editing.value) : null;

        return (
          <li
            key={row.id}
            className={`px-3.5 py-3 rounded-lg border shadow-sm ${
              isPending ? 'bg-skyTint/40 border-primary/20' : 'bg-red-50/60 border-red-200'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-gray-900 truncate">{row.subject || '(no subject)'}</h4>
                {isPending ? (
                  <p className="text-xs text-navy mt-0.5 inline-flex items-center gap-1">
                    <SafeIcon icon={FiClock} className="w-3 h-3 text-primary" />
                    Scheduled for {formatScheduledEt(row.send_at)}
                  </p>
                ) : (
                  <p className="text-xs text-red-700 mt-0.5 inline-flex items-start gap-1">
                    <SafeIcon icon={FiAlertTriangle} className="w-3 h-3 mt-0.5 shrink-0" />
                    {scheduledEmailProblem(row)}
                  </p>
                )}
                {isPending && row.attempts > 0 ? (
                  <p className="text-[11px] text-warning mt-0.5">
                    Retrying after a failed attempt ({row.attempts} of 3)
                  </p>
                ) : null}
                {row.to_email ? <p className="text-[11px] text-gray-400 mt-0.5 truncate">To {row.to_email}</p> : null}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {busy ? <Spinner className="text-primary" /> : null}
                {isPending ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      disabled={busy}
                      onClick={() => setEditing({ id: row.id, value: isoToScheduleValue(row.send_at) })}
                    >
                      Reschedule
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      disabled={busy}
                      onClick={() => run(row.id, () => onCancel(row.id))}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => dismiss(row.id)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-navy hover:bg-white"
                  >
                    <SafeIcon icon={FiX} className="w-3 h-3" /> Dismiss
                  </button>
                )}
              </div>
            </div>

            {isPending && editing?.id === row.id ? (
              <div className="mt-2.5 pt-2.5 border-t border-primary/15 space-y-2">
                <ScheduleTimePicker
                  value={editing.value}
                  onChange={(value) => setEditing({ id: row.id, value })}
                  disabled={busy}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    className="h-7"
                    disabled={busy || !editCheck?.iso}
                    onClick={() =>
                      run(row.id, async () => {
                        const ok = await onReschedule(row.id, editCheck.iso);
                        if (ok) setEditing(null);
                      })
                    }
                  >
                    Save new time
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={() => setEditing(null)}>
                    Keep current time
                  </Button>
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
